<template>
    <CompassDialog
        :visible.sync="showModal"
        size="small"
        class="catRatePopupManager"
        :title="
            tempRateCategory.id !== undefined
                ? $l10n.__('Modification d’une catégorie tarifaire')
                : $l10n.__(`Ajout d’une catégorie tarifaire`)
        "
        :closeOnClickOverlay="false"
        @closed="$emit('close')"
    >
        <template slot="default">
            <div class="dialog__default">
                <!--------------------INFO CATEGORIE TARIFAIRE-------------------->
                <div class="mb-3 typographies-heading-h3">
                    {{ $l10n.__('Information catégorie tarifaire') }}
                </div>
                <!-- Nom de la catégorie -->
                <div class="mb-1 typographies-heading-h4">
                    {{ $l10n.__('Nom de la catégorie') }}
                </div>
                <el-input
                    v-model="tempRateCategory.label"
                    class="wp-10"
                    @input="onLabelValueChange"
                />
                <p v-if="displayErrorOnInputEmpty" class="pt-1" size="small" type="alert">
                    {{ $l10n.__('Ce champ doit être renseigné') }}
                </p>
                <SwitchWithLabel
                    :textLabel="
                        $t.__('settings.taxes.popup.CatRatePopupManager.document_including_tax')
                    "
                    :value="tempRateCategory.includes_taxes"
                    class="mb-3 mt-3"
                    @change="updateIncludesTaxes($event)"
                />

                <!-- TVA à appliquer -->
                <div class="mb-1 typographies-heading-h4">
                    {{ $l10n.__('TVA à appliquer') }}
                </div>
                <el-select
                    v-model="tempRateCategory.tax_id"
                    class="mb-3 wp-10"
                    popperClass="customDropdownCatRatePopupManager"
                >
                    <el-option
                        :value="null"
                        :label="$t.__('settings.taxes.popup.CatRatePopupManager.product_tax')"
                    />
                    <el-option
                        v-for="item in listNoEcoTaxes"
                        :key="item.id"
                        :label="item | taxeLabel"
                        :value="item.id"
                    >
                    </el-option>
                </el-select>

                <!-- Devise -->
                <div class="mb-1 typographies-heading-h4">{{ $l10n.__('Devise') }}</div>
                <el-select
                    v-model="tempRateCategory.currency"
                    class="mb-3 wp-10"
                    popperClass="customDropdownCatRatePopupManager"
                >
                    <el-option
                        :value="null"
                        :label="$t.__('settings.taxes.popup.CatRatePopupManager.account_currency')"
                    />
                    <el-option
                        v-for="item in currencies"
                        :key="item.label"
                        :label="item.label"
                        :value="item.label"
                    />
                </el-select>

                <!-- Code compta vente -->
                <template v-if="accountingEnable">
                    <div class="mb-1 typographies-heading-h4">
                        {{ $l10n.__('Code compta vente') }}
                    </div>
                    <el-select
                        ref="selectAccountingCode"
                        v-model="tempRateCategory.accounting.accounting_code_id"
                        class="mb-3 wp-10"
                        popperClass="customDropdownCatRatePopupManager"
                    >
                        <el-option
                            :value="null"
                            :label="
                                $t.__('settings.taxes.popup.CatRatePopupManager.accounting_code')
                            "
                        />
                        <el-option
                            v-for="item in accountingCodes"
                            :key="item.value"
                            :label="item | accountingCodeLabel"
                            :value="item.value"
                        >
                        </el-option>
                    </el-select>
                </template>

                <!-- Code compta remises vente -->
                <template v-if="accountingEnable">
                    <div class="mb-1 typographies-heading-h4">
                        {{ $l10n.__('Code compta remises vente') }}
                    </div>
                    <el-select
                        ref="selectAccountingDiscountCode"
                        v-model="tempRateCategory.accounting.discount_accounting_code_id"
                        :disabled="!accountingCodeForDiscount"
                        class="mb-6 wp-10"
                        popperClass="customDropdownCatRatePopupManager"
                    >
                        <el-option
                            :value="null"
                            :label="
                                $t.__(
                                    'settings.taxes.popup.CatRatePopupManager.default_accounting_code',
                                )
                            "
                        />
                        <el-option
                            v-for="item in accountingCodes"
                            :key="item.value"
                            :label="item | accountingCodeLabel"
                            :value="item.value"
                        >
                        </el-option>
                    </el-select>
                </template>

                <!--------------------APPARENCE PAR DEFAUT-------------------->
                <div class="mb-3 typographies-heading-h3">
                    {{ $l10n.__('Apparence par défaut') }}
                </div>

                <!-- Apparence des devis -->
                <template v-if="layoutCanBeEstimates">
                    <div class="mb-1 typographies-heading-h4">
                        {{ $l10n.__('Apparence des devis') }}
                    </div>
                    <el-select
                        ref="selectLayoutEstimate"
                        v-model="tempRateCategory.default_layouts.estimates.id"
                        class="mb-3 wp-10"
                        popperClass="customDropdownCatRatePopupManager"
                    >
                        <el-option
                            :value="null"
                            :label="
                                $t.__(
                                    'settings.taxes.popup.CatRatePopupManager.quote_default_appearance',
                                )
                            "
                        />
                        <el-option
                            v-for="item in documentLayouts"
                            :key="item.value"
                            :label="item.label"
                            :value="item.value"
                        >
                        </el-option>
                    </el-select>
                </template>

                <!-- Apparence des factures -->
                <template v-if="layoutCanBeInvoices">
                    <div class="mb-1 typographies-heading-h4">
                        {{ $l10n.__('Apparence des factures') }}
                    </div>
                    <el-select
                        ref="selectLayoutInvoice"
                        v-model="tempRateCategory.default_layouts.invoices.id"
                        class="mb-3 wp-10"
                        popperClass="customDropdownCatRatePopupManager"
                    >
                        <el-option
                            :value="null"
                            :label="
                                $t.__(
                                    'settings.taxes.popup.CatRatePopupManager.invoice_default_appearance',
                                )
                            "
                        />
                        <el-option
                            v-for="item in documentLayouts"
                            :key="item.value"
                            :label="item.label"
                            :value="item.value"
                        >
                        </el-option>
                    </el-select>
                </template>

                <!-- Apparence des avoirs -->
                <template v-if="layoutCanBeCreditNotes">
                    <div class="mb-1 typographies-heading-h4">
                        {{ $l10n.__('Apparence des avoirs') }}
                    </div>
                    <el-select
                        ref="selectLayoutCreditNote"
                        v-model="tempRateCategory.default_layouts.credit_notes.id"
                        class="mb-3 wp-10"
                        popperClass="customDropdownCatRatePopupManager"
                    >
                        <el-option
                            :value="null"
                            :label="
                                $t.__(
                                    'settings.taxes.popup.CatRatePopupManager.credit_note_default_appearance',
                                )
                            "
                        />
                        <el-option
                            v-for="item in documentLayouts"
                            :key="item.value"
                            :label="item.label"
                            :value="item.value"
                        >
                        </el-option>
                    </el-select>
                </template>

                <!-- Apparence des bons de livraison -->
                <template v-if="layoutCanBeDeliveries">
                    <div class="mb-1 typographies-heading-h4">
                        {{ $l10n.__('Apparence des bons de livraison') }}
                    </div>
                    <el-select
                        v-if="layoutCanBeDeliveries"
                        ref="selectLayoutDelivery"
                        v-model="tempRateCategory.default_layouts.deliveries.id"
                        class="mb-3 wp-10"
                        popperClass="customDropdownCatRatePopupManager"
                    >
                        <el-option
                            :value="null"
                            :label="
                                $t.__(
                                    'settings.taxes.popup.CatRatePopupManager.delivery_note_default_appearance',
                                )
                            "
                        />
                        <el-option
                            v-for="item in documentLayouts"
                            :key="item.value"
                            :label="item.label"
                            :value="item.value"
                        >
                        </el-option>
                    </el-select>
                </template>

                <!-- Apparence des bons de commande -->
                <template v-if="layoutCanBeOrders">
                    <div class="mb-1 typographies-heading-h4">
                        {{ $l10n.__('Apparence des bons de commande') }}
                    </div>
                    <el-select
                        ref="selectLayoutOrder"
                        v-model="tempRateCategory.default_layouts.orders.id"
                        class="mb-3 wp-10"
                        popperClass="customDropdownCatRatePopupManager"
                    >
                        <el-option
                            :value="null"
                            :label="
                                $t.__(
                                    'settings.taxes.popup.CatRatePopupManager.order_default_appearance',
                                )
                            "
                        />
                        <el-option
                            v-for="item in documentLayouts"
                            :key="item.value"
                            :label="item.label"
                            :value="item.value"
                        >
                        </el-option>
                    </el-select>
                </template>

                <!-- Apparence des proformas -->
                <template v-if="layoutCanBeProformas">
                    <div class="mb-1 typographies-heading-h4">
                        {{ $l10n.__('Apparence des proformas') }}
                    </div>
                    <el-select
                        ref="selectLayoutProforma"
                        v-model="tempRateCategory.default_layouts.proformas.id"
                        class="mb-3 wp-10"
                        popperClass="customDropdownCatRatePopupManager"
                    >
                        <el-option
                            :value="null"
                            :label="
                                $t.__(
                                    'settings.taxes.popup.CatRatePopupManager.proforma_default_appearance',
                                )
                            "
                        />
                        <el-option
                            v-for="item in documentLayouts"
                            :key="item.value"
                            :label="item.label"
                            :value="item.value"
                        >
                        </el-option>
                    </el-select>
                </template>

                <!-- Apparence des modèles -->
                <template v-if="layoutCanBeModels">
                    <div class="mb-1 typographies-heading-h4">
                        {{ $l10n.__('Apparence des modèles') }}
                    </div>
                    <el-select
                        ref="selectLayoutModel"
                        v-model="tempRateCategory.default_layouts.models.id"
                        class="mb-6 wp-10"
                        popperClass="customDropdownCatRatePopupManager"
                    >
                        <el-option
                            :value="null"
                            :label="
                                $t.__(
                                    'settings.taxes.popup.CatRatePopupManager.template_default_appaearance',
                                )
                            "
                        />
                        <el-option
                            v-for="item in documentLayouts"
                            :key="item.value"
                            :label="item.label"
                            :value="item.value"
                        >
                        </el-option>
                    </el-select>
                </template>

                <!--------------------GESTION DES TARIFS-------------------->
                <div class="mb-3 typographies-heading-h3">
                    {{ $l10n.__('Gestion des tarifs') }}
                </div>

                <div class="flex-column">
                    <div class="flex-row ai-center mb-1">
                        <el-radio
                            ref="radioValue"
                            v-model="tempRateCategory.price.type"
                            class="m-0 flex-row ai-center"
                            label="value"
                        >
                            <span>
                                {{ $l10n.__('Appliquer un montant par produit') }}
                            </span>
                        </el-radio>
                    </div>
                    <label for="tarifsManagerRadio1">
                        <p
                            id="subInfoTarifsManagerRadio"
                            class="gray-80 typographies-paragraph-small-regular"
                        >
                            {{
                                $l10n.__(
                                    'Si aucun montant n’a été spécifié, le montant de référence du produit sera appliqué',
                                )
                            }}
                        </p>
                    </label>
                </div>

                <div class="flex-row ai-center">
                    <el-radio
                        ref="radioPercent"
                        v-model="tempRateCategory.price.type"
                        class="m-0 flex-row ai-center"
                        name="tarifsManagerRadio"
                        label="percent"
                    >
                        <span>
                            {{
                                $l10n.__('Appliquer un pourcentage du prix de référence du produit')
                            }}
                        </span>
                    </el-radio>
                </div>

                <template v-if="tempRateCategory.price.type === 'percent'">
                    <div class="mb-1 mt-2 typographies-heading-h4">
                        {{ $l10n.__('Pourcentage à appliquer') }}
                    </div>
                    <el-input
                        v-model="tempRateCategory.price.value"
                        max="500"
                        min="-500"
                        class="rate_input_percent"
                        @change="onPriceValueChange"
                    >
                        <template slot="append">%</template>
                    </el-input>
                    <p
                        v-if="displayErrorOnInputValue"
                        class="pt-1 typographies-paragraph-small-regular alert-red"
                    >
                        {{
                            $l10n.__(
                                'Merci de rentrer une valeur valide (pas plus de deux décimales)',
                            )
                        }}
                    </p>
                </template>
            </div>
        </template>
        <template slot="footer">
            <div class="dialog__footer">
                <CompassButton
                    :label="$t.__('common.actions.cancel')"
                    type="secondary"
                    @click="$emit('close')"
                />
                <CompassButton
                    :disabled="cantSubmit"
                    :label="$t.__('common.actions.save')"
                    class="ml-3"
                    @click="onSubmitForm"
                />
            </div>
        </template>
        <preloader :display="loading" />
    </CompassDialog>
</template>

<script>
import { createNamespacedHelpers } from 'vuex';
import Preloader from 'vcomponents/utilities/Preloader.vue';
import SwitchWithLabel from 'vcomponents/inputs/SwitchWithLabel.vue';

const rateCategories = createNamespacedHelpers('rateCategories');

export default {
    components: {
        Preloader,
        SwitchWithLabel,
    },
    filters: {
        taxeLabel(value) {
            return value.label ? `${value.label} - ${value.rate} %` : `${value.rate} %`;
        },
        accountingCodeLabel(value) {
            return value.label ? `${value.code} - ${value.label}` : `${value.code}`;
        },
    },
    props: {
        rateCategory: {
            type: Object,
            default() {
                return {
                    label: '',
                    includes_taxes: false,
                    currency: null,
                    tax_id: null,
                    accounting: {
                        accounting_code_id: null,
                        discount_accounting_code_id: null,
                    },
                    default_layouts: {
                        estimates: {
                            id: null,
                        },
                        invoices: {
                            id: null,
                        },
                        credit_notes: {
                            id: null,
                        },
                        deliveries: {
                            id: null,
                        },
                        orders: {
                            id: null,
                        },
                        proformas: {
                            id: null,
                        },
                        models: {
                            id: null,
                        },
                    },
                    price: {
                        type: 'value',
                        value: null,
                    },
                };
            },
        },
    },
    data() {
        return {
            showModal: true,
            loading: false,
            cantSubmit: false,
            tempRateCategory: {},
            displayErrorOnInputEmpty: false,
            displayErrorOnInputValue: false,
            validatorDefaultRate: /^(-?\d+[.,]?\d{0,2})$/,
        };
    },
    computed: {
        listNoEcoTaxes() {
            return this.$store.getters['vat/listNoEcoTaxes'];
        },
        defaultVatId() {
            return this.$store.getters['vat/taxesSettings'].vat_id;
        },
        rateCategoriesMetadata() {
            return this.$store.getters['rateCategories/rateCategoriesMetadata'];
        },
        layoutCanBeEstimates() {
            return this.rateCategoriesMetadata?.available_layouts.includes('estimates');
        },
        layoutCanBeInvoices() {
            return this.rateCategoriesMetadata?.available_layouts.includes('invoices');
        },
        layoutCanBeCreditNotes() {
            return this.rateCategoriesMetadata?.available_layouts.includes('credit_notes');
        },
        layoutCanBeDeliveries() {
            return this.rateCategoriesMetadata?.available_layouts.includes('deliveries');
        },
        layoutCanBeOrders() {
            return this.rateCategoriesMetadata?.available_layouts.includes('orders');
        },
        layoutCanBeProformas() {
            return this.rateCategoriesMetadata?.available_layouts.includes('proformas');
        },
        layoutCanBeModels() {
            return this.rateCategoriesMetadata?.available_layouts.includes('models');
        },
        currencies() {
            return this.rateCategoriesMetadata?.currencies;
        },
        accountingCodes() {
            return this.rateCategoriesMetadata?.accounting?.accounting_codes;
        },
        documentLayouts() {
            return this.rateCategoriesMetadata?.document_layouts;
        },
        accountingEnable() {
            return this.rateCategoriesMetadata?.accounting?.enabled;
        },
        accountingCodeForDiscount() {
            return this.rateCategoriesMetadata?.accounting?.use_specific_code_for_discount;
        },
    },
    created() {
        this.tempRateCategory = _.cloneDeep(this.rateCategory);
    },
    methods: {
        ...rateCategories.mapActions(['updateRateCategory', 'postRateCategory']),
        onSubmitForm() {
            this.cantSubmit = true;
            if (this.tempRateCategory.price.type === 'value') {
                this.tempRateCategory.price.value = null;
            } else if (
                !this.tempRateCategory.price.value ||
                this.tempRateCategory.price.value === ''
            ) {
                this.tempRateCategory.price.value = 0;
            }

            if (
                !this.tempRateCategory.price.value ||
                this.validatorDefaultRate.test(this.tempRateCategory.price.value)
            ) {
                if (this.tempRateCategory.label.length) {
                    this.tempRateCategory.price.value = String(
                        this.tempRateCategory.price.value,
                    ).replace(',', '.');
                    this.tempRateCategory.id
                        ? this.sendUpdateRateCategory()
                        : this.sendPostRateCategory();
                } else {
                    this.displayErrorOnInputEmpty = true;
                    this.cantSubmit = false;
                }
            } else {
                this.displayErrorOnInputValue = true;
                this.cantSubmit = false;
            }
        },

        sendUpdateRateCategory() {
            this.isLoading = true;
            this.updateRateCategory(this.tempRateCategory)
                .then(() => {
                    Push.success(this.$l10n.__('Les modifications ont bien été prises en compte.'));
                    this.$emit('close');
                })
                .catch(() => {
                    Push.error(this.$l10n.__("Une erreur est survenue lors de l'enregistrement."));
                })
                .finally(() => {
                    this.isLoading = false;
                });
        },
        sendPostRateCategory() {
            this.isLoading = true;
            this.postRateCategory(this.tempRateCategory)
                .then(() => {
                    Push.success(this.$l10n.__('Les modifications ont bien été prises en compte.'));
                    this.$emit('close');
                })
                .catch(() => {
                    Push.error(this.$l10n.__("Une erreur est survenue lors de l'enregistrement."));
                })
                .finally(() => {
                    this.isLoading = false;
                });
        },
        updateIncludesTaxes(value) {
            this.tempRateCategory.includes_taxes = value;
        },
        onPriceValueChange() {
            this.cantSubmit = false;
            this.displayErrorOnInputValue = false;
        },
        onLabelValueChange() {
            this.cantSubmit = false;
            this.displayErrorOnInputEmpty = false;
        },
    },
};
</script>

<style lang="scss">
@import '~@sellsy/styleguide/src/_variables';

.catRatePopupManager {
    .dialog {
        &__header {
            display: flex;
            justify-content: space-between;
            padding: 24px 32px;

            &--title {
                display: flex;
                flex-direction: column;
            }

            &--close {
                display: flex;
                justify-content: flex-end;
                align-items: flex-start;
            }
        }

        &__default {
            padding-bottom: 24px;

            .alert-red {
                color: var(--colors-common-text-alert-default);
            }
        }

        &__footer {
            display: flex;
            justify-content: flex-end;
            padding: 0 32px 24px 32px;
        }
    }

    .rate_input_percent {
        .el-input__inner {
            border-right: none;
        }

        .el-input-group__append {
            background: inherit;
            color: inherit;
        }

        .el-input__inner:hover + .el-input-group__append {
            border-color: $grey;
        }

        .el-input__inner:focus + .el-input-group__append {
            border-color: $primary-50;
        }
    }
}

.customDropdownCatRatePopupManager.el-select-dropdown.el-popper {
    box-shadow: 0 0 12px rgba(17, 17, 17, 0.24);
    margin-top: 4px;

    .popper__arrow {
        display: none;
    }
}
</style>
